import type { Location } from "@/app/hooks/useLocations";

export interface NominatimAddress {
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  province?: string;
  state?: string;
  county?: string;
  country?: string;
  country_code?: string;
}

/** Common Nominatim country labels → English names used in listings. */
const LOCALIZED_COUNTRY_NAMES: Record<string, string> = {
  españa: "Spain",
  espana: "Spain",
  deutschland: "Germany",
  österreich: "Austria",
  osterreich: "Austria",
  schweiz: "Switzerland",
  suisse: "Switzerland",
  svizzera: "Switzerland",
  nederland: "Netherlands",
  belgië: "Belgium",
  belgie: "Belgium",
  belgique: "Belgium",
  brasil: "Brazil",
  méxico: "Mexico",
  mexico: "Mexico",
  日本: "Japan",
  "대한민국": "South Korea",
  "south korea": "South Korea",
  "united states of america": "United States",
  "united states": "United States",
  "united kingdom": "United Kingdom",
  "côte d'ivoire": "Ivory Coast",
  "cote d'ivoire": "Ivory Coast",
};

const COUNTRY_CODE_TO_ENGLISH: Record<string, string> = {
  es: "Spain",
  de: "Germany",
  at: "Austria",
  ch: "Switzerland",
  nl: "Netherlands",
  be: "Belgium",
  br: "Brazil",
  mx: "Mexico",
  jp: "Japan",
  kr: "South Korea",
  us: "United States",
  gb: "United Kingdom",
  fr: "France",
  it: "Italy",
  pt: "Portugal",
  gr: "Greece",
  th: "Thailand",
  id: "Indonesia",
  ph: "Philippines",
  eg: "Egypt",
  mv: "Maldives",
  au: "Australia",
  nz: "New Zealand",
  cr: "Costa Rica",
  hr: "Croatia",
  cy: "Cyprus",
  mt: "Malta",
  co: "Colombia",
};

export function localizedCountryToEnglish(country: string): string {
  const trimmed = country.trim();
  if (!trimmed) return "";
  const key = trimmed.toLowerCase();
  return LOCALIZED_COUNTRY_NAMES[key] || trimmed;
}

function englishCountryFromAddress(address: NominatimAddress): string {
  const code = address.country_code?.toLowerCase();
  if (code && COUNTRY_CODE_TO_ENGLISH[code]) {
    return COUNTRY_CODE_TO_ENGLISH[code];
  }
  if (address.country) {
    return localizedCountryToEnglish(address.country);
  }
  return "";
}

/** Ordered candidates from Nominatim — province/state before small towns. */
export function nominatimAddressCandidates(address: NominatimAddress): string[] {
  const country = englishCountryFromAddress(address);
  const parts = [
    address.province,
    address.state,
    address.county,
    address.city,
    address.town,
    address.village,
    address.municipality,
    country,
  ];

  return Array.from(
    new Set(parts.map((p) => p?.trim()).filter(Boolean) as string[]),
  );
}

export function resolveNearbyLocationFromNominatim(
  address: NominatimAddress,
  searchLocations: (query: string, limit?: number) => Location[],
): string {
  for (const candidate of nominatimAddressCandidates(address)) {
    const matches = searchLocations(candidate, 1);
    if (matches[0]?.displayName) {
      return matches[0].displayName;
    }
  }

  return (
    address.province ||
    address.state ||
    address.county ||
    address.city ||
    address.town ||
    address.village ||
    address.municipality ||
    englishCountryFromAddress(address) ||
    ""
  );
}
